import React from "react"; // Removed useState
import "./OurServicesSection.css";

// ✅ Import the new ServiceItemComp
import ServiceItemComp from '../../service/ServiceItemComp/ServiceItemComp';
import CLINIC_CONFIG from '../../../config/clinicConfig';


// All icon and data imports remain here
import { ReactComponent as DentalCleaningIcon } from '../../../assets/icons/dental.svg';
import { ReactComponent as DentalSealantsIcon } from '../../../assets/icons/dentalsealants.svg';
import { ReactComponent as ToothExtractionsIcon } from '../../../assets/icons/toothextractions.svg';
import { ReactComponent as FluorideTreatmentsIcon } from '../../../assets/icons/flouridetreatments.svg';
import { ReactComponent as CustomMouthguardsIcon } from '../../../assets/icons/custommouthguard.svg';
import { ReactComponent as DigitalXrayIcon } from '../../../assets/icons/digital-xrays.svg';
import { ReactComponent as OralCancerIcon } from '../../../assets/icons/oral-cancer-screening.svg';
import { ReactComponent as HygieneEducationIcon } from '../../../assets/icons/hygiene-education.svg';
import { ReactComponent as CariesTestIcon } from '../../../assets/icons/caries.svg';
import { ReactComponent as PediatricExamsIcon } from '../../../assets/icons/pediatric-exams.svg';
import { ReactComponent as SpaceMaintainersIcon } from '../../../assets/icons/spacemainteners.svg';
import { ReactComponent as OrthodonticEvaluationsIcon } from '../../../assets/icons/biteissues.svg';
import { ReactComponent as InvisalignIcon } from '../../../assets/icons/invisalign.svg';
import { ReactComponent as TeethWhiteningIcon } from '../../../assets/icons/teethwhitening.svg';
import { ReactComponent as VeneersIcon } from '../../../assets/icons/veneers.svg';
import { ReactComponent as DentalImplantsIcon } from '../../../assets/icons/dentalimplants.svg';
import { ReactComponent as BondingIcon } from '../../../assets/icons/bonding.svg';
import { ReactComponent as CosmeticCrownsIcon } from '../../../assets/icons/cosmetic-crowns.svg';
import { ReactComponent as SmileMakeoverIcon } from '../../../assets/icons/smile-makeover.svg';
import { ReactComponent as ToothReshapingIcon } from '../../../assets/icons/toothreshaping.svg';
import { ReactComponent as CrownsIcon } from '../../../assets/icons/crowns&bridges.svg';
import { ReactComponent as RootCanalIcon } from '../../../assets/icons/rootcanaltreatment.svg';
import { ReactComponent as InlayOnlayIcon } from '../../../assets/icons/inlay-onlay.svg';
import { ReactComponent as PulpotomiesIcon } from '../../../assets/icons/pulpotomies.svg';
import { ReactComponent as WisdomToothIcon } from '../../../assets/icons/wisdom-tooth.svg';
import { ReactComponent as EmergencyIcon } from '../../../assets/icons/emergency.svg';
import { ReactComponent as FillingRepairIcon } from '../../../assets/icons/filling-repair.svg';
import { ReactComponent as InflammationIcon } from '../../../assets/icons/inflammation.svg';
import { ReactComponent as DentalTraumaIcon } from '../../../assets/icons/dental-trauma.svg';
// Location suffix for SEO-friendly service URLs
const locationSuffix = `-${CLINIC_CONFIG.city.toLowerCase()}-${CLINIC_CONFIG.stateShortForm.toLowerCase()}`;

// ✅ Export the type so other files can import it
export type ServiceItem = {
  icon: string | React.FC<React.SVGProps<SVGSVGElement>>;
  condition: string;
  treatment: string;
  path: string;
};

type ServiceGroup = {
  category: string;
  title: string;
  services: ServiceItem[];
};

// All data remains here
const serviceGroups: ServiceGroup[] = [
  {
    category: "PREVENTATIVE SERVICES",
    title: "RESOLVE",
    services: [
      { icon: DentalCleaningIcon, condition: "Got Plaque", treatment: "Dental Cleaning", path: `/dental-cleaning-and-prevention${locationSuffix}` },
      { icon: DentalSealantsIcon, condition: "Cavities", treatment: "Dental Sealants", path: "/dental-sealants" },
      { icon: ToothExtractionsIcon, condition: "Damaged tooth", treatment: "Tooth Extractions", path: "/routine-tooth-extractions" },
      { icon: FluorideTreatmentsIcon, condition: "Weak Enamel", treatment: "Fluoride Treatment", path: "/fluoride-treatments" },
      { icon: CustomMouthguardsIcon, condition: "Sports Injury", treatment: "Custom Mouthguards", path: `/custom-sports-mouthguards${locationSuffix}` },
      { icon: DigitalXrayIcon, condition: "Hidden Cavities", treatment: "Digital X-rays", path: `/dental-exams-and-cleanings${locationSuffix}` },
      { icon: OralCancerIcon, condition: "Mouth Sores", treatment: "Oral Cancer Screening", path: `/oral-cancer-screening${locationSuffix}` },
      { icon: HygieneEducationIcon, condition: "Poor oral hygiene", treatment: "Hygiene Education", path: "/oral-hygiene-instructions" },
      { icon: CariesTestIcon, condition: "High Risk", treatment: "Caries Susceptibility Test", path: "/caries-susceptibility-test" },
      { icon: PediatricExamsIcon, condition: "Early Growth", treatment: "Pediatric Exams", path: `/pediatric-dentistry${locationSuffix}` },
      { icon: SpaceMaintainersIcon, condition: "Tooth Loss", treatment: "Space Maintainers", path: "/space-maintainers" },
      { icon: OrthodonticEvaluationsIcon, condition: "Bite Issues", treatment: "Orthodontic Evaluations", path: `/orthodontics${locationSuffix}` },
    ],
  },
  {
    category: "TRANSFORMATIVE SERVICES",
    title: "RENEW",
    services: [
      { icon: InvisalignIcon, condition: "Crooked Teeth", treatment: "Invisalign", path: "/invisalign" },
      { icon: TeethWhiteningIcon, condition: "Yellow Teeth", treatment: "Teeth Whitening", path: "/teeth-whitening" },
      { icon: VeneersIcon, condition: "Tooth Gap", treatment: "Veneers", path: "/veneers" },
      { icon: DentalImplantsIcon, condition: "Missing Tooth", treatment: "Dental Implants", path: `/dental-implants${locationSuffix}` },
      { icon: BondingIcon, condition: "Chipping teeth", treatment: "Dental Bonding", path: "/bonding" },
      { icon: CosmeticCrownsIcon, condition: "Worn Tooth", treatment: "Cosmetic Crowns", path: "/cosmetic-dentistry" },
      { icon: SmileMakeoverIcon, condition: "Smile Flaws", treatment: "Smile Makeovers", path: "/perfectly-aligned-teeth" },
      { icon: ToothReshapingIcon, condition: "Uneven Edges", treatment: "Tooth Reshaping", path: "/cosmetic-dentistry" }
    ],
  },
  {
    category: "RESTORATIVE SERVICES",
    title: "RESTORE",
    services: [
      { icon: RootCanalIcon, condition: "Tooth Pain", treatment: "Root Canal Treatment", path: `/root-canal-treatment${locationSuffix}` },
      { icon: InlayOnlayIcon, condition: "Large Cavity", treatment: "Inlay Onlay Restoration", path: `/inlay-onlay-restorations${locationSuffix}` },
      { icon: CrownsIcon, condition: "Cracked Tooth", treatment: "Dental Crowns", path: "/crowns" },
      { icon: PulpotomiesIcon, condition: "Baby Toothache", treatment: "Pulpotomies", path: `/pulpotomies${locationSuffix}` },
      { icon: WisdomToothIcon, condition: "Impacted Teeth", treatment: "Wisdom Tooth Removal", path: "/oral-surgical-extractions" },
      { icon: EmergencyIcon, condition: "Urgent Care", treatment: "Emergency Dentistry", path: `/emergency-services${locationSuffix}` },
      { icon: FillingRepairIcon, condition: "Lost Filling", treatment: "Crown or Filling Repair", path: `/fillings${locationSuffix}` },
      { icon: InflammationIcon, condition: "Bleeding Gums", treatment: "Inflammation Treatment", path: `/gingivitis${locationSuffix}` },
      { icon: DentalTraumaIcon, condition: "Dental Trauma", treatment: "Injury Treatment", path: `/emergency-services${locationSuffix}` }

    ]
  },
];

// ❌ ServiceItemComp definition is removed from here

const OurServicesSection: React.FC = () => {
  return (
    <section className="our-services-section py-5 bg-light">
      <div className="container">
        <h2 className="text-center section-main-title mb-5 text-dark fade-in">Our Dental Services in {CLINIC_CONFIG.city}, {CLINIC_CONFIG.stateShortForm}</h2>
        <div className="row gy-5">
          {serviceGroups.map((group, idx) => (
            <div
              key={idx}
              className="col-12 col-lg-4 d-flex flex-column fade-in"
              style={{ animationDelay: `${idx * 0.2}s`, animationFillMode: "both" }}
            >
              <p className="pre-title text-uppercase text-muted text-center fs-4">{group.category}</p>
              <h3 className="service-group-title mb-3 text-center fw-bold fs-3">{group.title}</h3>
              <div className="services-container">
                <ul className="service-list list-unstyled list-group">
                  {group.services.map((item, index) => (
                    // ✅ Use the imported component
                    <ServiceItemComp key={index} {...item} />
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurServicesSection;